import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { ScoreCell } from '../data/scorecard_insights';
import { DS, numericSx } from './Dashboard';

/*****************************************************************************
 ** Classic scorecard marks: circles under par, squares over par,
 ** doubled for two or more strokes. An ace is a filled gold circle.
*****************************************************************************/

type MarkKind = 'ace' | 'eagle' | 'birdie' | 'par' | 'bogey' | 'double' | 'none';

const markKind = ({ strokes, toPar }: ScoreCell): MarkKind => {
    if (strokes === null || toPar === null) return 'none';
    if (strokes === 1) return 'ace';
    if (toPar <= -2) return 'eagle';
    if (toPar === -1) return 'birdie';
    if (toPar === 0) return 'par';
    if (toPar === 1) return 'bogey';
    return 'double';
};

const MARK_STYLE: Record<MarkKind, { color: string; round: boolean; double: boolean; fill?: string }> = {
    ace:    { color: DS.gold, round: true, double: false, fill: DS.gold },
    eagle:  { color: DS.info, round: true, double: true },
    birdie: { color: DS.gain, round: true, double: false },
    par:    { color: 'transparent', round: false, double: false },
    bogey:  { color: DS.loss, round: false, double: false },
    double: { color: DS.loss, round: false, double: true },
    none:   { color: 'transparent', round: false, double: false },
};

export const MARK_LEGEND: { kind: MarkKind; label: string }[] = [
    { kind: 'ace', label: 'Ace' },
    { kind: 'eagle', label: 'Eagle+' },
    { kind: 'birdie', label: 'Birdie' },
    { kind: 'bogey', label: 'Bogey' },
    { kind: 'double', label: 'Double+' },
];

const Mark: React.FC<{ kind: MarkKind; size: number; children?: React.ReactNode; muted?: boolean }> = ({ kind, size, children, muted }) => {
    const style = MARK_STYLE[kind];
    // The second ring sits outside the first, separated by a surface-coloured gap
    const ring = style.double ? `0 0 0 2px ${DS.surface}, 0 0 0 3.5px ${style.color}` : 'none';
    return (
        <Box sx={{
            width: size,
            height: size,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: style.round ? '50%' : '4px',
            border: `1.5px solid ${style.color}`,
            boxShadow: ring,
            background: style.fill ?? (kind === 'none' || kind === 'par' ? 'transparent' : alpha(style.color, 0.12)),
            color: style.fill ? DS.surface : muted ? DS.textFaint : DS.text,
            fontSize: size * 0.46,
            fontWeight: 700,
            lineHeight: 1,
            ...numericSx,
        }}>
            {children}
        </Box>
    );
};

export const ScoreMark: React.FC<{ cell: ScoreCell; size?: number }> = ({ cell, size = 28 }) => (
    <Mark kind={markKind(cell)} size={size} muted={cell.strokes === null}>
        {cell.strokes ?? '–'}
    </Mark>
);

export const ScoreMarkLegend: React.FC = () => (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {MARK_LEGEND.map(l => (
            <Box key={l.kind} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Mark kind={l.kind} size={14} />
                <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>{l.label}</Typography>
            </Box>
        ))}
    </Box>
);
